import {
  convertDateToYYYYMMDDFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  formatDataToLabelValue,
  PageWrapper,
  Select,
} from '@repo/ui';
import FeatherIcon from 'feather-icons-react';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardText,
  CardTitle,
  Col,
  Label,
  Row,
  Spinner,
} from 'reactstrap';

import { useSuspectedLocationManipulation } from '../../../hooks/useSuspectedLocationManipulation';
import { EmptySuspectedState } from './components/empty-state';
import { SuspectedReportCardLoading } from './components/loading';
import { SuspectedSummary } from './components/suspected-summary';
import {
  StudentsRankingTable,
  SuspectedActivitiesTable,
  SuspectedTableActions,
} from './components/tables';

export const SuspectedLocationManipulation = () => {
  const {
    data,
    validation,
    hasSearched,
    appliedFilters,
    isLoadingSubmit,
    studentsSelectOptions,
    exportRankingPdf,
    exportRankingExcel,
    exportActivitiesPdf,
    exportActivitiesExcel,
  } = useSuspectedLocationManipulation();

  return (
    <PageWrapper title="Indícios de Manipulação de Localização">
      <div className="mb-4">
        <h4>Atividades com Indícios de Manipulação de Localização</h4>
        <p className="text-muted mb-0">
          Atividades onde o aplicativo detectou possível localização simulada
          (GPS falso).
        </p>
      </div>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col className="mb-3" sm={12} md={4} lg={4}>
                <Label htmlFor="student" className="form-label">
                  Estudante
                </Label>
                <Select
                  isClearable
                  id="student"
                  name="student"
                  placeholder="Todos os Estudantes"
                  value={validation.values.student}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('student', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    studentsSelectOptions as never,
                    'id',
                    'name',
                  )}
                />
              </Col>

              <Col sm={12} md={4} lg={4} className="mb-3">
                <Row>
                  <Label htmlFor="initial-date" className="form-label">
                    Período
                  </Label>
                  <CustomDateRangePicker
                    placement="auto"
                    onDateChange={(start, ends) => {
                      validation.setFieldValue(
                        'starts',
                        convertDateToYYYYMMDDFormat(start),
                      );
                      validation.setFieldValue(
                        'ends',
                        convertDateToYYYYMMDDFormat(ends),
                      );
                    }}
                  />
                </Row>
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <Button color="primary" type="submit" disabled={isLoadingSubmit}>
                {isLoadingSubmit ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </Button>
            </div>
          }
        />
      </Card>

      {!hasSearched ? (
        <EmptySuspectedState />
      ) : isLoadingSubmit ? (
        <SuspectedReportCardLoading />
      ) : (
        <>
          <SuspectedSummary summary={data.suspectedManipulationCount} />

          <Card className="p-0">
            <CardHeader className="bg-white border-0 pb-2">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <CardTitle className="d-flex align-items-center gap-2 mb-1">
                    <FeatherIcon icon="bar-chart-2" size={20} />
                    <span>Ranking de Estudantes</span>
                  </CardTitle>

                  <CardText className="text-muted mb-0">
                    Ranking dos estudantes com base nas atividades analisadas.
                  </CardText>
                </div>

                <SuspectedTableActions
                  disabled={data.suspectedStudentsRanking.length === 0}
                  isLoadingExcel={exportRankingExcel.loading}
                  isLoadingPdf={exportRankingPdf.loading}
                  handleExcel={() => {
                    exportRankingExcel.request({
                      ...appliedFilters,
                      type: 'excel',
                    });
                  }}
                  handlePdf={() => {
                    exportRankingPdf.request({
                      ...appliedFilters,
                      type: 'pdf',
                    });
                  }}
                />
              </div>
            </CardHeader>

            <CardBody className="pt-2 pb-3 px-0">
              <StudentsRankingTable
                data={data.suspectedStudentsRanking}
                isLoading={isLoadingSubmit}
                hasFetchedData={hasSearched}
              />
            </CardBody>
          </Card>

          <Card className="p-0">
            <CardHeader className="bg-white border-0 pb-2">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <CardTitle className="d-flex align-items-center gap-2 mb-1">
                    <FeatherIcon icon="alert-triangle" size={20} />
                    <span>Atividades Suspeitas</span>
                  </CardTitle>

                  <CardText className="text-muted mb-0">
                    Atividades que apresentam indícios de uso de localização GPS
                    falsa.
                  </CardText>
                </div>

                <SuspectedTableActions
                  disabled={data.suspectedManipulation.length === 0}
                  isLoadingExcel={exportActivitiesExcel.loading}
                  isLoadingPdf={exportActivitiesPdf.loading}
                  handleExcel={() => {
                    exportActivitiesExcel.request({
                      ...appliedFilters,
                      type: 'excel',
                    });
                  }}
                  handlePdf={() => {
                    exportActivitiesPdf.request({
                      ...appliedFilters,
                      type: 'pdf',
                    });
                  }}
                />
              </div>
            </CardHeader>

            <CardBody className="pt-2 pb-3 px-0">
              <SuspectedActivitiesTable
                data={data.suspectedManipulation}
                isLoading={isLoadingSubmit}
                hasFetchedData={hasSearched}
              />
            </CardBody>
          </Card>
        </>
      )}
    </PageWrapper>
  );
};
